import React, { createContext, useContext, useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import { useGoogleLogin } from '@react-oauth/google';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../../components/notification/NotificationContext';
import { authService } from '@/services/auth';
import { useAuth } from '@/common/auth/hooks/useAuth';

interface GoogleSignInValue {
  /** Start the Google flow. No-op when sign-in is not configured. */
  signIn: () => void;
  /** True between the click and either navigation or an error. */
  isLoading: boolean;
  /** Desktop builds hand OAuth to the system browser. */
  isElectron: boolean;
  /** False when VITE_GOOGLE_CLIENT_ID is absent — hide the CTAs. */
  isConfigured: boolean;
}

const GoogleSignInContext = createContext<GoogleSignInValue>({
  signIn: () => {},
  isLoading: false,
  isElectron: false,
  isConfigured: false,
});

/**
 * The one live authentication action, shared by every entry point on the
 * landing page — the header link, the hero CTA and the closing CTA.
 *
 * It is published through context rather than returned from a plain hook
 * because `useGoogleLogin` throws outside `GoogleOAuthProvider`, and that
 * provider is only mounted when a client ID is configured (`App.tsx`). A hook
 * called unconditionally from a page would crash an unconfigured build; a
 * provider can decline to call it.
 */
export const useGoogleSignIn = (): GoogleSignInValue =>
  useContext(GoogleSignInContext);

/** Inner half — only mounted when `GoogleOAuthProvider` is guaranteed above. */
const ConfiguredGoogleSignIn: React.FC<{
  clientId: string;
  children: React.ReactNode;
}> = ({ clientId, children }) => {
  const { showError } = useNotification();
  const navigate = useNavigate();
  const { login: loginContext } = useAuth();
  const isElectron = !!window.electronAPI;
  const [isLoading, setIsLoading] = useState(false);

  const handleAuthWithBackend = async (oauthPayload: {
    email: string;
    providerAccountId: string;
    profile: {
      given_name?: string;
      family_name?: string;
      name?: string;
      picture?: string;
      avatar_url?: string;
    };
    accessToken?: string;
    refreshToken?: string;
    expiresAt?: number;
  }) => {
    setIsLoading(true);
    try {
      const auth = await authService.oauthLogin({
        email: oauthPayload.email,
        provider: 'google',
        providerAccountId: oauthPayload.providerAccountId,
        profile: oauthPayload.profile,
        accessToken: oauthPayload.accessToken,
        refreshToken: oauthPayload.refreshToken,
        expiresAt: oauthPayload.expiresAt,
      });

      await loginContext(auth);
      await new Promise((resolve) => setTimeout(resolve, 50));
      navigate('/projects');
    } catch (error) {
      setIsLoading(false);
      throw error;
    }
  };

  const handleOAuthSignIn = useGoogleLogin({
    scope: 'openid email profile',
    prompt: 'select_account',
    onSuccess: async (tokenResponse) => {
      try {
        // Fetch user profile from Google
        const profileResponse = await fetch(
          'https://www.googleapis.com/oauth2/v2/userinfo',
          {
            headers: {
              Authorization: `Bearer ${tokenResponse.access_token}`,
            },
          }
        );

        if (!profileResponse.ok) {
          throw new Error('Failed to fetch Google profile');
        }

        const profile = await profileResponse.json();

        await handleAuthWithBackend({
          email: profile.email,
          providerAccountId: profile.id,
          profile: {
            given_name: profile.given_name,
            family_name: profile.family_name,
            name: profile.name,
            picture: profile.picture,
          },
          accessToken: tokenResponse.access_token,
          refreshToken: (tokenResponse as any).refresh_token,
          expiresAt: tokenResponse.expires_in
            ? Date.now() + tokenResponse.expires_in * 1000
            : undefined,
        });
      } catch (error) {
        console.error('OAuth login error:', error);
        showError(
          'Google sign-in failed. Please try again or use email/password.'
        );
      }
    },
    onError: () => {
      showError('Google sign-in was cancelled or failed.');
      setIsLoading(false);
    },
  });

  const handleElectronOAuthSignIn = async () => {
    try {
      if (!window.electronAPI) {
        throw new Error('Electron API unavailable');
      }
      if (!clientId) {
        throw new Error('Google client ID is not configured');
      }

      const result = await window.electronAPI.startGoogleOAuthLogin(clientId);

      await handleAuthWithBackend({
        email: result.email,
        providerAccountId: result.providerAccountId,
        profile: {
          given_name: result.profile.given_name,
          family_name: result.profile.family_name,
          name: result.profile.name,
          picture: result.profile.picture,
        },
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        expiresAt: result.expiresAt,
      });
    } catch (error) {
      console.error('Electron OAuth login error:', error);
      showError(
        'Google sign-in failed. Please try again or use a different account.'
      );
      setIsLoading(false);
    }
  };

  return (
    <GoogleSignInContext.Provider
      value={{
        signIn: () =>
          isElectron ? handleElectronOAuthSignIn() : handleOAuthSignIn(),
        isLoading,
        isElectron,
        isConfigured: true,
      }}
    >
      {children}
    </GoogleSignInContext.Provider>
  );
};

/**
 * Wrap any surface that offers sign-in. Without a configured client ID it
 * provides `isConfigured: false` so callers can hide their CTAs rather than
 * render buttons that do nothing.
 */
export const GoogleSignInProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;

  if (!clientId) {
    return <>{children}</>;
  }

  return (
    <ConfiguredGoogleSignIn clientId={clientId}>
      {children}
    </ConfiguredGoogleSignIn>
  );
};

/** The MUI-styled button, as used by the pre-landing sign-in card. */
const GoogleSignInButtonInner: React.FC = () => {
  const { signIn, isLoading, isElectron, isConfigured } = useGoogleSignIn();

  if (!isConfigured) {
    return null;
  }

  return (
    <Button
      fullWidth
      variant="contained"
      color="success"
      disabled={isLoading}
      startIcon={
        isLoading ? (
          <CircularProgress size={18} color="inherit" />
        ) : (
          <GoogleIcon />
        )
      }
      onClick={signIn}
      sx={{
        py: 1.5,
        fontSize: '1rem',
        fontWeight: 600,
        textTransform: 'none',
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none',
          transform: 'translateY(-1px)',
        },
      }}
    >
      {isLoading
        ? 'Signing in…'
        : isElectron
          ? 'Sign in with Browser'
          : 'Continue with Google'}
    </Button>
  );
};

/** Self-contained: brings its own provider, so it works anywhere. */
export const GoogleSignInButton: React.FC = () => (
  <GoogleSignInProvider>
    <GoogleSignInButtonInner />
  </GoogleSignInProvider>
);

export default GoogleSignInProvider;
